import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, CircularProgress, IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';

const ChatBox = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const washerId = searchParams.get('washerId');
  const userName = searchParams.get('userName');

  console.log('ChatBox userId:', userId, 'washerId:', washerId);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (userId) {
      fetchMessages();
    }
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
      const response = await fetch(`${process.env.REACT_APP_SERVER_URI}/users/chats/${userId}?washerId=${washerId || ''}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });
      const result = await response.json();
      setMessages(result || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
      const url = `${process.env.REACT_APP_SERVER_URI}/users/chats/reply/${userId}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          washerId: washerId || null,
          message: newMessage,
          sentBy: 'admin'
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setMessages(prev => [...prev, result]);
        setNewMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
    setSending(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchMessages();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">
            Chat - User Name: {userName} 
          </Typography>
          {washerId && <Typography variant="body2" color="text.secondary">Washer ID: {washerId}</Typography>}
        </Box>
        <IconButton onClick={handleRefresh} color="primary" title="Refresh messages">
          <RefreshIcon />
        </IconButton>
      </Box>
      
      {/* Messages Container */}
      <Paper 
        sx={{ 
          flex: 1, 
          p: 2, 
          mb: 2, 
          overflow: 'auto',
          maxHeight: 'calc(80vh - 120px)',
          display: 'flex',
          flexDirection: 'column-reverse'
        }}
      >
        <div ref={messagesEndRef} />
        {messages.length === 0 ? (
          <Typography color="text.secondary" align="center">
            No messages yet. Start the conversation!
          </Typography>
        ) : (
          [...messages].map((message, index) => (
            <Box key={message._id || index} sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: (message.sentBy === 'admin' || message.sentBy === 'washer') ? 'flex-end' : 'flex-start',
                  mb: 1
                }}
              >
                <Box
                  sx={{
                    maxWidth: '70%',
                    bgcolor: (message.sentBy === 'admin' || message.sentBy === 'washer') ? 'primary.main' : 'grey.200',
                    color: (message.sentBy === 'admin' || message.sentBy === 'washer') ? 'white' : 'text.primary',
                    p: 1.5,
                    borderRadius: 2,
                    wordBreak: 'break-word'
                  }}
                >
                  <Typography variant="body1">{message.message}</Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      opacity: 0.7,
                      display: 'block',
                      mt: 0.5,
                      fontSize: '0.75rem'
                    }}
                  >
                    {formatTime(message.createdAt)} - {message.sentBy}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Paper>

      {/* Message Input */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          multiline
          maxRows={3}
          placeholder="Type your message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={sending}
        />
        <Button
          variant="contained"
          onClick={sendMessage}
          disabled={!newMessage.trim() || sending}
          sx={{ minWidth: 50 }}
        >
          {sending ? <CircularProgress size={20} /> : <SendIcon />}
        </Button>
      </Box>
    </Box>
  );
};

export default ChatBox;